// Copyright (c) 2020 by Audere
//
// Use of this source code is governed by an LGPL-3.0 license that
// can be found in the LICENSE file distributed with this file.

import {
  DummyEvent,
  Question,
  QuestionValidator,
  isSelectQuestion,
  isTextQuestion,
} from "./QuestionnaireTypes";
import { useCallback, useContext, useLayoutEffect, useState } from "react";

import { QuestionContext } from "./Questionnaire";
import { RENDER_COMPONENTS } from "./QuestionnaireRenderComponents";
import React from "react";
import { getCursorPosition } from "./QuestionnaireUtils";

export default (props: Question) => {
  // API from Questionnaire
  const {
    registerQuestion,
    getQuestions,
    getQuestionUpdaters,
    unregisterQuestion,
  } = useContext(QuestionContext);
  // The component to render the field.
  const InputComponent = RENDER_COMPONENTS[props.type];

  // We keep question value in a state, so that subsequent renders don't
  // overwrite the updated values.
  const [questionInternal, setQuestionInternal] = useState(props);

  // This callback will be sent to the Questionnaire, and used when another
  // QuestionnaireInput wants to update this one.
  // Examples: address typeahead or password/password repeat.
  const updateQuestion = useCallback(
    (newQuestion: Question) => {
      if (
        // We only need to update if one of those two changed.
        newQuestion.value === questionInternal.value &&
        newQuestion.error === questionInternal.error
      ) {
        return;
      }

      // Update internal state
      setQuestionInternal(newQuestion);
      // register new values with the Questionnaire.
      registerQuestion(newQuestion, updateQuestion);
    },
    [questionInternal, registerQuestion]
  );

  // Called back when the InputComponent updates
  const onElementChange = useCallback(
    (event: React.ChangeEvent | React.MouseEvent | DummyEvent) => {
      const target = event.currentTarget as HTMLInputElement;
      const allQuestions = { ...getQuestions() };
      const allQuestionUpdaters = getQuestionUpdaters();
      let relevantQuestion = { ...allQuestions[questionInternal.name] };

      allQuestions[questionInternal.name] = relevantQuestion;

      let newValue = target.value;

      if (isTextQuestion(relevantQuestion) && relevantQuestion.formatter) {
        // Format the value
        const formattedValue = relevantQuestion.formatter(
          newValue,
          allQuestions,
          relevantQuestion
        );

        // Maintain cursor position.
        if (formattedValue !== newValue) {
          let cursorStart = target.selectionStart;
          let cursorEnd = target.selectionEnd;
          if (cursorStart !== null || cursorEnd !== null) {
            cursorStart = Math.max(cursorStart || 0, cursorEnd || 0);

            const newCursorPosition = getCursorPosition({
              nonFormattedValue: newValue,
              formattedValue: formattedValue,
              cursorPosition: cursorStart,
            });

            cursorStart = cursorEnd = newCursorPosition;
            setTimeout(() => {
              if (cursorStart !== null && cursorEnd !== null) {
                target.setSelectionRange(cursorStart, cursorEnd);
              }
            });
          }
        }
        newValue = formattedValue;
      }
      relevantQuestion.value = newValue;

      if (isTextQuestion(relevantQuestion) && relevantQuestion.typeahead) {
        if (relevantQuestion.value) {
          relevantQuestion.typeahead.callFunction(
            relevantQuestion,
            question => {
              // This will return asynchronously.
              relevantQuestion = { ...getQuestions()[relevantQuestion.name] };
              if (
                isTextQuestion(relevantQuestion) &&
                isTextQuestion(question)
              ) {
                relevantQuestion.typeahead!.blob = question.typeahead!.blob;
              }
              setQuestionInternal(relevantQuestion);
              registerQuestion(relevantQuestion, updateQuestion);
            },
            (err: Error) => {
              console.log(
                `Typeahead error for ${relevantQuestion.name}: ` + err.message
              );
            }
          );
        } else {
          relevantQuestion.typeahead = { ...relevantQuestion.typeahead };
          relevantQuestion.typeahead.blob = undefined;
          relevantQuestion.typeahead.values = [];
        }
      }

      // Validate the value
      relevantQuestion.validators &&
        relevantQuestion.validators.find(
          (validator: QuestionValidator): boolean => {
            relevantQuestion.error = validator(
              relevantQuestion.value || "",
              allQuestions,
              relevantQuestion
            );
            if (relevantQuestion.error) {
              return true;
            }
            return false;
          }
        );

      setQuestionInternal(relevantQuestion);
      registerQuestion(relevantQuestion, updateQuestion);

      const questionNames = Object.getOwnPropertyNames(allQuestions);

      // Update the values of the other fields. Other fields could have
      // been updated during the formatting or validating steps.
      questionNames.forEach(questionName => {
        if (questionName !== relevantQuestion.name)
          allQuestionUpdaters[questionName](allQuestions[questionName]);
      });
    },
    [
      getQuestionUpdaters,
      getQuestions,
      questionInternal.name,
      registerQuestion,
      updateQuestion,
    ]
  );

  useLayoutEffect(() => {
    registerQuestion(questionInternal, updateQuestion);

    return () => {
      unregisterQuestion(questionInternal);
    };
  }, [questionInternal, registerQuestion, unregisterQuestion, updateQuestion]);

  return (
    <InputComponent
      question={{
        ...questionInternal,
        ...(isSelectQuestion(props)
          ? {
              selectOptions: props.selectOptions,
            }
          : {}),
      }}
      onChange={onElementChange}
    />
  );
};
